import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Save, Loader2, Search } from "lucide-react";
import { api } from "../../lib/api";

// iter-208 — Admin keyword editor for the trade-filter.
// Lives in the AdminPage under tab "keywords". Lets a superadmin
// add/remove extra keywords per plugin, and toggle hardcoded terms
// on/off (via a `disabled` list) without redeploying. The filter in
// /app/backend/trade_filter.py#get_merged_trade_keywords consumes
// this when the Estimator does its fast skim pass.
export default function KeywordsTab() {
    const [plugins, setPlugins] = useState([]);
    const [defaultTrades, setDefaultTrades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [filter, setFilter] = useState("");
    const [newTerm, setNewTerm] = useState("");
    const [saving, setSaving] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/plugin-keywords");
            setPlugins(data.items || []);
            setDefaultTrades(data.default_filter_trades || []);
            if (!active && (data.items || []).length) setActive(data.items[0].plugin_id);
        } catch (e) {
            toast.error("Failed to load plugin keywords");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

    const current = useMemo(
        () => plugins.find((p) => p.plugin_id === active) || null,
        [plugins, active],
    );

    const [customDraft, setCustomDraft] = useState([]);
    const [disabledDraft, setDisabledDraft] = useState([]);

    useEffect(() => {
        setCustomDraft(current?.custom || []);
        setDisabledDraft(current?.disabled || []);
        setNewTerm("");
    }, [active, current?.updated_at]);

    const addTerm = () => {
        const t = (newTerm || "").trim().toLowerCase();
        if (!t) return;
        if (customDraft.includes(t)) {
            toast.message("Already in the custom list");
            return;
        }
        setCustomDraft([t, ...customDraft]);
        setNewTerm("");
    };

    const removeCustom = (term) => {
        setCustomDraft(customDraft.filter((t) => t !== term));
    };

    const toggleDisabled = (term) => {
        if (disabledDraft.includes(term)) {
            setDisabledDraft(disabledDraft.filter((t) => t !== term));
        } else {
            setDisabledDraft([term, ...disabledDraft]);
        }
    };

    const persist = async () => {
        if (!current) return;
        setSaving(true);
        try {
            await api.put(`/admin/plugin-keywords/${current.plugin_id}`, {
                custom: customDraft,
                disabled: disabledDraft,
            });
            toast.success("Keywords saved");
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const toggleDefaultTrade = async (pluginId) => {
        const next = defaultTrades.includes(pluginId)
            ? defaultTrades.filter((t) => t !== pluginId)
            : [...defaultTrades, pluginId];
        setDefaultTrades(next);
        try {
            await api.put("/admin/plugin-keywords/default-trades", { trades: next });
            toast.success(`${pluginId.replace("_", " ")} ${next.includes(pluginId) ? "added to" : "removed from"} default filter`);
        } catch (e) {
            toast.error("Failed to update default filter");
            await reload();
        }
    };

    const hardcodedFiltered = useMemo(() => {
        if (!current) return [];
        const q = filter.trim().toLowerCase();
        return (current.hardcoded || []).filter((t) => !q || t.includes(q));
    }, [current, filter]);

    if (loading) {
        return (
            <div className="text-slate-400 text-sm py-6" data-testid="keywords-loading">
                <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…
            </div>
        );
    }

    return (
        <section className="space-y-4" data-testid="keywords-tab">
            <div className="rounded-sm border border-[#2A3655] bg-[#0F1B3A] p-4">
                <h3 className="text-sm font-black uppercase tracking-wider text-white mb-1">
                    Trade Keyword Filter
                </h3>
                <p className="text-xs text-slate-400 leading-snug max-w-3xl">
                    When a user runs an estimate scoped to a trade (e.g. Earthwork, Septic, Landscaping),
                    the extractor fast-skims each page's text and only sends pages that mention one of these
                    keywords (or that look like a drawing) to the LLM. On a 2000-page MTO tender this drops
                    hours of extraction down to minutes. Edits here apply instantly — no redeploy needed.
                </p>
            </div>

            {/* iter-211 — Default auto-apply trade list. */}
            <div className="rounded-sm border border-[#00A3FF]/40 bg-[#0F1B3A] p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div>
                        <h3 className="text-sm font-black uppercase tracking-wider text-[#8ad0ff]">
                            Default Auto-Filter Trades
                        </h3>
                        <p className="text-xs text-slate-300 leading-snug max-w-3xl">
                            Trades below are <b>always applied</b> to every estimate — even if the user hasn't picked a scope. Union of their keyword banks forms the auto-filter. Toggle any off to exclude it.
                        </p>
                    </div>
                    <span className="text-[10px] text-slate-400 tabular-nums">
                        {defaultTrades.length} enabled
                    </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    {plugins.map((p) => {
                        const on = defaultTrades.includes(p.plugin_id);
                        return (
                            <button
                                key={p.plugin_id}
                                onClick={() => toggleDefaultTrade(p.plugin_id)}
                                data-testid={`keywords-default-${p.plugin_id}`}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold border transition-colors ${
                                    on
                                        ? "bg-[#00A3FF]/20 border-[#00A3FF] text-white"
                                        : "border-[#2A3655] bg-[#0A1128]/50 text-slate-400 hover:border-[#00A3FF]/60"
                                }`}
                            >
                                <span className={`inline-block w-2 h-2 rounded-full ${on ? "bg-emerald-400" : "bg-slate-600"}`} />
                                {p.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Plugin tabs */}
            <div className="flex flex-wrap gap-2">
                {plugins.map((p) => {
                    const dirty = active === p.plugin_id &&
                        (JSON.stringify(customDraft) !== JSON.stringify(p.custom || []) ||
                            JSON.stringify(disabledDraft) !== JSON.stringify(p.disabled || []));
                    return (
                        <button
                            key={p.plugin_id}
                            onClick={() => setActive(p.plugin_id)}
                            data-testid={`keywords-plugin-${p.plugin_id}`}
                            className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border transition-colors ${
                                active === p.plugin_id
                                    ? "bg-[#007AFF]/20 border-[#00A3FF] text-white"
                                    : "border-[#2A3655] text-slate-300 hover:text-white hover:border-[#00A3FF]/60"
                            }`}
                        >
                            {p.label}
                            <span className="ml-2 text-[9px] text-slate-400">
                                {p.hardcoded.length + (p.custom?.length || 0) - (p.disabled?.length || 0)}
                                {dirty && <span className="text-amber-300 ml-1">●</span>}
                            </span>
                        </button>
                    );
                })}
            </div>

            {current && (
                <div className="grid md:grid-cols-2 gap-4">
                    {/* Custom (user-added) */}
                    <div className="rounded-sm border border-[#2A3655] bg-[#0F1B3A] p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className="text-xs uppercase font-black tracking-wider text-white">
                                    Custom Keywords
                                </h4>
                                <p className="text-[10px] text-slate-400 mt-0.5">
                                    Add extra terms specific to {current.label.toLowerCase()} jobs you do.
                                </p>
                            </div>
                            <span
                                className="text-xs bg-[#00A3FF]/15 text-[#8ad0ff] px-2 py-0.5 rounded-sm"
                                data-testid="keywords-custom-count"
                            >
                                {customDraft.length}
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <input
                                value={newTerm}
                                onChange={(e) => setNewTerm(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") addTerm(); }}
                                placeholder={'e.g. 3-stage filter bed, hydrovac, 4" clear stone'}
                                className="flex-1 bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm text-sm text-white outline-none px-3 py-1.5"
                                data-testid="keywords-new-term"
                            />
                            <button
                                onClick={addTerm}
                                disabled={!newTerm.trim()}
                                className="px-3 py-1.5 text-xs font-bold rounded-sm bg-[#00A3FF] hover:bg-[#1DB2FF] text-white disabled:opacity-40 flex items-center gap-1"
                                data-testid="keywords-add-btn"
                            >
                                <Plus className="w-3 h-3" /> Add
                            </button>
                        </div>
                        {customDraft.length === 0 ? (
                            <div className="text-xs text-slate-500 italic">No custom keywords yet.</div>
                        ) : (
                            <div className="flex flex-wrap gap-1.5 max-h-80 overflow-y-auto">
                                {customDraft.map((t) => (
                                    <span
                                        key={t}
                                        className="inline-flex items-center gap-1 bg-[#00A3FF]/15 border border-[#00A3FF]/40 text-[#8ad0ff] px-2 py-0.5 rounded-sm text-xs"
                                        data-testid={`keywords-custom-${t}`}
                                    >
                                        {t}
                                        <button
                                            onClick={() => removeCustom(t)}
                                            className="hover:text-rose-300"
                                            aria-label={`Remove ${t}`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Hardcoded (toggle on/off) */}
                    <div className="rounded-sm border border-[#2A3655] bg-[#0F1B3A] p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className="text-xs uppercase font-black tracking-wider text-white">
                                    Hardcoded Keywords
                                </h4>
                                <p className="text-[10px] text-slate-400 mt-0.5">
                                    Click any to disable — the filter will stop matching on it.
                                    Disabled count: {disabledDraft.length}.
                                </p>
                            </div>
                            <div className="relative">
                                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder="Filter…"
                                    className="pl-6 pr-2 py-1 bg-[#0A1128] border border-[#2A3655] rounded-sm text-xs text-white outline-none w-32"
                                    data-testid="keywords-hardcoded-filter"
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-80 overflow-y-auto">
                            {hardcodedFiltered.map((t) => {
                                const off = disabledDraft.includes(t);
                                return (
                                    <button
                                        key={t}
                                        onClick={() => toggleDisabled(t)}
                                        data-testid={`keywords-hard-${t}`}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs border transition-colors ${
                                            off
                                                ? "border-rose-500/40 bg-rose-500/10 text-rose-300 line-through"
                                                : "border-[#2A3655] bg-[#0A1128]/60 text-slate-200 hover:border-[#00A3FF]/60"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                );
                            })}
                            {hardcodedFiltered.length === 0 && (
                                <div className="text-xs text-slate-500 italic">
                                    No matches.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-3">
                <button
                    onClick={persist}
                    disabled={saving || !current}
                    data-testid="keywords-save"
                    className="px-4 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold flex items-center gap-2 disabled:opacity-40"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save {current?.label || ""} keywords
                </button>
                <button
                    onClick={() => {
                        if (!current) return;
                        setCustomDraft(current.custom || []);
                        setDisabledDraft(current.disabled || []);
                    }}
                    disabled={!current}
                    className="px-3 py-2 rounded-sm border border-[#2A3655] text-xs text-slate-300 hover:text-white hover:border-rose-500/40"
                    data-testid="keywords-revert"
                >
                    Revert
                </button>
                {current?.updated_at && (
                    <span className="text-xs text-slate-500 ml-auto">
                        Last saved {current.updated_at.slice(0, 19).replace("T", " ")}
                    </span>
                )}
            </div>
        </section>
    );
}
